/**
 * An environment's graph as something to play (`PlayDef`: which node kinds are played, which
 * link kinds lead on): a story read scene by scene, a prototype clicked screen by screen. Each
 * played node is a passage (its name, its page). Its choices are its pins, in order (worded by
 * the pin, leading where the pin's link goes, nowhere yet when it has none); a node without pins
 * offers its links of the choice kinds to other played nodes instead, in the graph's order,
 * worded by the link's label (else the name of where it leads). The story starts at the
 * passages nothing leads to.
 *
 * `storyHtml` writes the whole story as one HTML file that plays by itself in any browser,
 * with no app and no network: to share a demo.
 */
import type { Graph } from "@protocol/Graph";
import type { PlayDef } from "@protocol/PlayDef";
import { escapeHtml, markdownHtml } from "./markdown";

/** A way on from a passage. */
export interface Choice {
  /** What the reader picks. */
  label: string;
  /** The passage it leads to (`null`: a pin not wired yet). */
  to: string | null;
}

/** One played node. */
export interface Passage {
  /** The node's id. */
  id: string;
  /** Its name, the passage's title. */
  name: string;
  /** Its page (Markdown). */
  page: string;
  /** Where the reader may go from it. */
  choices: Choice[];
}

/** A graph to play. */
export interface Story {
  /** The passages by node id. */
  passages: Map<string, Passage>;
  /** Where it may start: the passages nothing leads to (all of them when every one is led to), in the graph's order. */
  starts: string[];
}

/** The story `graph` tells when played as `play` says (`textOf`: a node's text when it is not its page: its document's). */
export function storyOf(graph: Pick<Graph, "nodes" | "links">, play: PlayDef, textOf: (node: Graph["nodes"][number]) => string | null = () => null): Story {
  const played = graph.nodes.filter((n) => play.nodes.includes(n.kind));
  const passages = new Map<string, Passage>(played.map((n) => [n.id, { id: n.id, name: n.name, page: withoutTitle(textOf(n), n.name) ?? n.page ?? "", choices: [] }]));
  const led = new Set<string>();
  for (const n of played) {
    const from = passages.get(n.id)!;
    const pins = n.pins ?? [];
    if (pins.length) {
      for (const pin of pins) {
        const link = graph.links.find((l) => l.from === n.id && l.pin === pin.id && passages.has(l.to));
        from.choices.push({ label: pin.label.trim() || (link ? passages.get(link.to)!.name : pin.id), to: link?.to ?? null });
        if (link && link.to !== n.id) led.add(link.to);
      }
      continue;
    }
    for (const link of graph.links) {
      const to = link.from === n.id && !link.pin && play.choices.includes(link.kind) ? passages.get(link.to) : undefined;
      if (!to) continue;
      from.choices.push({ label: link.label.trim() || to.name, to: to.id });
      if (link.to !== n.id) led.add(link.to);
    }
  }
  const free = played.filter((n) => !led.has(n.id)).map((n) => n.id);
  return { passages, starts: free.length ? free : played.map((n) => n.id) };
}

/** A document's Markdown without its first line when that is the passage's own name (its title, shown apart). */
function withoutTitle(text: string | null, name: string): string | null {
  if (text === null) return null;
  const [first = "", ...rest] = text.split("\n");
  return first.replace(/^#+\s*/, "").trim().toLowerCase() === name.trim().toLowerCase() ? rest.join("\n").trim() : text;
}

/** What a passage's page looks like in the exported file: its HTML, mentions as plain words. */
function passageHtml(page: string): string {
  return markdownHtml(page);
}

/** The exported file's data: passages with their page as HTML. `<` written as `<`, so no text can close the script. */
function storyJson(story: Story, start: string): string {
  const passages = Object.fromEntries([...story.passages.values()].map((p) => [p.id, { name: p.name, html: passageHtml(p.page), choices: p.choices }]));
  return JSON.stringify({ start, passages }).replace(/</g, "\\u003c");
}

/** The player inside the exported file: shows a passage, its choices, and keeps the way back. */
const PLAYER = `
const story = JSON.parse(document.getElementById("story").textContent);
const trail = [];
const $ = (id) => document.getElementById(id);
function show(id) {
  const p = story.passages[id];
  if (!p) return;
  $("title").textContent = p.name;
  $("text").innerHTML = p.html;
  const list = $("choices");
  list.replaceChildren();
  for (const c of p.choices) {
    const b = document.createElement("button");
    b.textContent = c.label;
    if (c.to === null) b.disabled = true;
    else b.onclick = () => { trail.push(id); show(c.to); };
    list.append(b);
  }
  $("end").hidden = p.choices.length > 0;
  $("back").disabled = trail.length === 0;
  window.scrollTo(0, 0);
}
$("back").onclick = () => { if (trail.length) show(trail.pop()); };
$("restart").onclick = () => { trail.length = 0; show(story.start); };
show(story.start);
`;

/** The exported file's look: a page for reading, light or dark as the system is. */
const STYLE = `
:root { color-scheme: light dark; --bg: #fbf8f2; --fg: #24211c; --muted: #7a7266; --accent: #8a4b2a; --line: #e4ddd0; }
@media (prefers-color-scheme: dark) { :root { --bg: #1c1a17; --fg: #ece6db; --muted: #a39a8c; --accent: #e0a37a; --line: #3a352e; } }
body { margin: 0; background: var(--bg); color: var(--fg); font: 18px/1.65 Georgia, "Times New Roman", serif; }
main { max-width: 40rem; margin: 0 auto; padding: 3rem 1rem 4rem; }
header { color: var(--muted); font: 14px system-ui, sans-serif; display: flex; justify-content: space-between; gap: 1rem; }
h1#title { font-size: 2rem; margin: 1.5rem 0 1rem; }
#text p { white-space: pre-wrap; }
blockquote { border-left: 3px solid var(--line); margin-left: 0; padding-left: 1rem; color: var(--muted); }
.mention { color: var(--accent); }
#choices { display: grid; gap: .6rem; margin-top: 2rem; }
button { font: 16px system-ui, sans-serif; padding: .7rem 1rem; border-radius: 8px; border: 1px solid var(--line); background: transparent; color: var(--fg); text-align: left; cursor: pointer; }
#choices button:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
#choices button:disabled { opacity: .5; cursor: default; }
header button { padding: .2rem .6rem; font-size: 14px; }
#end { color: var(--muted); font-style: italic; margin-top: 2rem; }
`;

/**
 * The story as one HTML file that plays by itself, from passage `start` (by default the first
 * start), titled `title`. Every text is escaped; the passages' pages become HTML here, so the
 * file's script only shows them.
 */
export function storyHtml(story: Story, title: string, start = story.starts[0] ?? ""): string {
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${STYLE}</style>
</head>
<body>
<main>
<header><span>${escapeHtml(title)}</span><span><button id="back" type="button">Back</button> <button id="restart" type="button">Start again</button></span></header>
<h1 id="title"></h1>
<div id="text"></div>
<div id="choices"></div>
<p id="end" hidden>The end.</p>
</main>
<script type="application/json" id="story">${storyJson(story, start)}</script>
<script>${PLAYER}</script>
</body>
</html>
`;
}
